import React, {
  forwardRef,
  useState,
  useEffect,
  useRef
} from 'react'
import ReactSVG from 'react-svg'
import PropTypes from 'prop-types'

import tooltipIcon from '../../../images/tooltip-icon.svg'
import styles from './input.css'

/**
 * Input component with floating label and optional tooltip
 */
const Input = forwardRef(({
  label,
  error,
  value,
  onChange,
  onBlur,
  onFocus,
  disabled,
  type = 'text',
  name,
  autoComplete,
  className = '',
  tooltip,
  ...rest
}, ref) => {
  const [isFocused, setIsFocused] = useState(false)
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const tooltipRef = useRef(null)

  const hasValue = Boolean(value && value.length)
  const hasError = Boolean(error && error.length)

  // Close the tooltip when clicking outside of it
  useEffect(() => {
    // Handler to detect clicks outside the tooltip
    const handleOutsideClick = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setIsTooltipOpen(false)
      }
    }

    // Only attach listener if tooltip is open
    if (isTooltipOpen) {
      document.addEventListener('click', handleOutsideClick)
    }

    // Cleanup: remove the event listener
    return () => {
      document.removeEventListener('click', handleOutsideClick)
    }
  }, [isTooltipOpen])

  const handleFocus = (e) => {
    setIsFocused(true)
    onFocus?.(e)
  }

  const handleBlur = (e) => {
    setIsFocused(false)
    onBlur?.(e)
  }

  const handleTooltipMouseEnter = () => {
    setIsTooltipOpen(true)
  }

  const handleTooltipMouseLeave = () => {
    setIsTooltipOpen(false)
  }

  const handleTooltipClick = () => {
    setIsTooltipOpen(true)
  }

  const containerClass = [
    styles.inputContainer,
    hasError && styles.error,
    isFocused && styles.focused,
    disabled && styles.disabled,
    tooltip && styles.hasTooltip
  ].filter(Boolean).join(' ')

  const labelClass = [
    styles.label,
    (hasValue || isFocused) && styles.labelFloating
  ].filter(Boolean).join(' ')

  return (
    <div className={`${styles.fieldWrapper} ${className}`}>
      <div className={containerClass}>
        <label htmlFor={name} className={labelClass}>
          {label}
        </label>

        <input
          ref={ref}
          id={name}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          autoComplete={autoComplete}
          className={styles.input}
          {...rest}
        />

        {tooltip && (
          <div ref={tooltipRef}>
            <button
              type="button"
              className={styles.tooltipButton}
              onMouseEnter={handleTooltipMouseEnter}
              onMouseLeave={handleTooltipMouseLeave}
              onClick={handleTooltipClick}
              aria-label="More information"
            >
              <ReactSVG
                src={tooltipIcon}
                beforeInjection={svg => {
                  svg.setAttribute('class', styles.tooltipIcon)
                }}
              />

              {isTooltipOpen && (
                <div
                  className={styles.tooltipContent}
                  onMouseEnter={handleTooltipMouseEnter}
                  onMouseLeave={handleTooltipMouseLeave}
                >
                  <div className={styles.tooltipArrow} />
                  {tooltip}
                </div>
              )}
            </button>
          </div>
        )}
      </div>

      {hasError && <span className={styles.errorMessage}>{error}</span>}
    </div>
  )
})

Input.propTypes = {
  label: PropTypes.string.isRequired,
  error: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  disabled: PropTypes.bool,
  type: PropTypes.string,
  name: PropTypes.string.isRequired,
  autoComplete: PropTypes.string,
  className: PropTypes.string,
  tooltip: PropTypes.string
}

export default Input
